import type { Expurgo, ItemExpurgo, StatusExpurgo } from '@/types/privacidade'
import { PREFIXO_ALERTA_RECONCILIACAO, STATUS_REJEITAVEIS } from '@/types/privacidade'
import type { AcessoPrivacidade } from './comum'

/**
 * Dupla checagem do expurgo (AN-12 §5.2) — o servidor decide (CHECK no banco + serviço);
 * a tela só não oferece o botão a quem não pode, e diz por quê:
 *  - 1ª aprovação: só DPO, e não quem solicitou;
 *  - 2ª aprovação: só ADMIN, e nem quem solicitou nem quem deu a 1ª.
 * Três `sub` distintos — o mesmo usuário nunca aprova duas vezes.
 */
export type SituacaoAprovacao =
  | { tipo: 'pode'; etapa: 1 | 2 }
  | { tipo: 'jaAtuou'; mensagem: string }
  | { tipo: 'outroPapel'; mensagem: string }
  | { tipo: 'nenhuma' }

export const situacaoAprovacao = (e: Expurgo, acesso: AcessoPrivacidade): SituacaoAprovacao => {
  const eu = acesso.usuarioId
  // O servidor diz qual aprovação falta (`proximaAprovacao`); o status só é usado se ela não vier.
  const proxima =
    e.proximaAprovacao !== undefined
      ? e.proximaAprovacao
      : e.status === 'PENDENTE'
        ? 'DPO'
        : e.status === 'APROVADO_1'
          ? 'ADMIN'
          : null
  if (proxima === 'DPO') {
    if (eu && e.solicitadoPor === eu) {
      return {
        tipo: 'jaAtuou',
        mensagem: 'Você solicitou este expurgo: a 1ª aprovação precisa ser de outra pessoa (DPO).',
      }
    }
    if (!acesso.dpo) return { tipo: 'outroPapel', mensagem: 'Aguardando a 1ª aprovação, que é do DPO.' }
    return { tipo: 'pode', etapa: 1 }
  }
  if (proxima === 'ADMIN') {
    if (eu && (e.solicitadoPor === eu || e.aprovado1Por === eu)) {
      return {
        tipo: 'jaAtuou',
        mensagem:
          e.aprovado1Por === eu
            ? 'Você deu a 1ª aprovação: a 2ª precisa ser de outra pessoa (ADMIN). O mesmo usuário não aprova duas vezes.'
            : 'Você solicitou este expurgo: a 2ª aprovação precisa ser de outra pessoa (ADMIN).',
      }
    }
    if (!acesso.admin) return { tipo: 'outroPapel', mensagem: 'Aguardando a 2ª aprovação, que é do ADMIN.' }
    return { tipo: 'pode', etapa: 2 }
  }
  return { tipo: 'nenhuma' }
}

/** Rejeitar: DPO ou ADMIN, antes de EXECUTANDO (inclusive na carência). */
export const podeRejeitar = (status: StatusExpurgo, acesso: AcessoPrivacidade): boolean =>
  acesso.permitido && STATUS_REJEITAVEIS.includes(status)

/**
 * Cancelar: só o solicitante, antes da 1ª aprovação — em PENDENTE ou em BLOQUEADO_RETENCAO (o
 * hold zera as aprovações; o servidor aceita os dois com a 1ª aprovação nula).
 */
export const podeCancelar = (e: Expurgo, acesso: AcessoPrivacidade): boolean =>
  (e.status === 'PENDENTE' || e.status === 'BLOQUEADO_RETENCAO') &&
  !e.aprovado1Por &&
  !!acesso.usuarioId &&
  e.solicitadoPor === acesso.usuarioId

/**
 * Retirar item: só DPO, expurgo em EXECUTANDO, item em ERRO/PENDENTE e que o storage nunca tocou
 * (`possivelmenteExecutado`, vindo do servidor). O servidor confere tudo de novo.
 */
export const podeRetirarItem = (e: Expurgo, item: ItemExpurgo, acesso: AcessoPrivacidade): boolean =>
  acesso.permitido &&
  acesso.dpo &&
  e.status === 'EXECUTANDO' &&
  (item.status === 'ERRO' || item.status === 'PENDENTE') &&
  item.possivelmenteExecutado !== true

/**
 * Alerta da reconciliação: algum item com `ultimoErro` gravado pelo servidor como
 * `AGUARDANDO_CONFIRMACAO:<módulos>` (cópias sem confirmação, `documento.expurgado` republicado).
 * Leitura do campo, não cálculo.
 */
export const temAlertaReconciliacao = (e: Expurgo): boolean =>
  (e.itens ?? []).some((i) => (i.ultimoErro ?? '').startsWith(PREFIXO_ALERTA_RECONCILIACAO))

/** "1/2", "2/2" — contagem das aprovações registradas. */
export const contagemAprovacoes = (e: Expurgo): number => (e.aprovado1Por ? 1 : 0) + (e.aprovado2Por ? 1 : 0)
